import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Link,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { AuthScreenShell, AuthCard, AuthCardHeader } from './auth';
import { productBrand } from '../productBrand';
import { isLocalhost } from '../utils/isLocalhost';

const DEMO_PASSWORD_MASK = '••••••••••';

interface SignInProps {
    onSwitchToSignUp: () => void;
}

const SignIn: React.FC<SignInProps> = ({ onSwitchToSignUp }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const { signIn, signInWithDemoToken, isLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const demoParam = searchParams.get('demo');
    /** Set while the form holds the prefilled demo credentials; editing a field drops it. */
    const [demoToken, setDemoToken] = useState<string | null>(null);
    const [demoCompanyName, setDemoCompanyName] = useState<string | null>(null);

    useEffect(() => {
        if (!demoParam) return;
        let cancelled = false;
        apiService
            .getDemoLoginPreview(demoParam)
            .then((preview) => {
                if (cancelled) return;
                setEmail(preview.email);
                setPassword(DEMO_PASSWORD_MASK);
                setShowPassword(false);
                setDemoToken(demoParam);
                setDemoCompanyName(preview.companyName);
            })
            .catch(() => {
                if (!cancelled) setError('El enlace demo no es válido o fue desactivado');
            });
        return () => {
            cancelled = true;
        };
    }, [demoParam]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Por favor completa todos los campos');
            return;
        }

        try {
            if (demoToken) {
                await signInWithDemoToken(demoToken);
            } else {
                await signIn(email, password);
            }
        } catch (error) {
            setError('Email o contraseña incorrectos');
        }
    };

    return (
        <AuthScreenShell>
            <AuthCard>
                <AuthCardHeader
                    title="Bienvenid@ de vuelta"
                    subtitle={`Inicia sesión para acceder a ${productBrand.name}`}
                    titleSx={{ fontFamily: productBrand.fontFamily }}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {demoToken && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {demoCompanyName
                            ? `Acceso demo para ${demoCompanyName}. `
                            : 'Acceso demo. '}
                        Tus credenciales ya están completas, solo inicia sesión.
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Correo electrónico"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (demoToken) {
                                setDemoToken(null);
                                setPassword('');
                            }
                        }}
                        disabled={isLoading}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                            if (demoToken) {
                                setDemoToken(null);
                                setPassword('');
                                return;
                            }
                            setPassword(e.target.value);
                        }}
                        disabled={isLoading}
                        InputProps={{
                            endAdornment: demoToken ? undefined : (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                        <Link
                            component={RouterLink}
                            to="/auth/olvido-contrasena"
                            variant="body2"
                            sx={{ color: 'primary.main' }}
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </Box>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2, mb: 2, py: 1.5 }}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Iniciar Sesión'
                        )}
                    </Button>

                    {isLocalhost() && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                ¿No tienes una cuenta?{' '}
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={onSwitchToSignUp}
                                    sx={{ cursor: 'pointer', color: 'primary.main' }}
                                >
                                    Regístrate
                                </Link>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </AuthCard>
        </AuthScreenShell>
    );
};

export default SignIn;
